import { useCallback, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { colors } from '../../themes/colors';
import { sizes } from '../../themes/sizes';
import { t } from '../../locales/i18n';
import { AuthError, requestSignIn, verifyCode } from '../../auth/auth-store';
import { SocketClient } from '../../web-socket/socket-client';

/**
 * Sign-in, replacing SuperTokens' prebuilt UI.
 *
 * One screen with two steps rather than two routes: on iOS the magic link
 * frequently opens in Mail's in-app browser, which is a different context with
 * no session, so the code path has to stay visible and usable in the tab the
 * user already has open. That is also why the code entry is shown immediately
 * after the request instead of being offered as a fallback.
 */

const Screen = styled.main`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100dvh;
  padding: ${sizes.padding.huge};
  gap: 1.5rem;
`;

const Brand = styled.h1`
  color: ${colors.primary};
  font-size: 2.5rem;
  margin: 0;
  text-align: center;
`;

const Lead = styled.p`
  color: ${colors.grey};
  margin: 0;
  text-align: center;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Input = styled.input`
  border: ${sizes.borderWidth.thin} solid ${colors.greyLightest};
  border-radius: ${sizes.borderRadius.normal};
  font-size: 1.1rem;
  padding: 0.9rem;
  width: 100%;

  &:focus {
    border-color: ${colors.primary};
    outline: none;
  }
`;

const CodeInput = styled(Input)`
  font-family: ui-monospace, 'Courier New', monospace;
  font-size: 1.6rem;
  letter-spacing: 0.4rem;
  text-align: center;
`;

const Button = styled.button`
  background: ${colors.primary};
  border: none;
  border-radius: ${sizes.borderRadius.normal};
  color: ${colors.white};
  font-size: 1.1rem;
  padding: 0.9rem;

  &:disabled {
    background: ${colors.disabled};
    color: ${colors.greyLighter};
  }
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${colors.grey};
  padding: 0.5rem;
  text-decoration: underline;
`;

const ErrorText = styled.p`
  color: ${colors.error};
  margin: 0;
  text-align: center;
`;

type Step = 'email' | 'code';

export function SignInPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onRequest = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      setBusy(true);
      setError(null);
      try {
        await requestSignIn(email.trim());
        setStep('code');
      } catch (err) {
        setError(
          err instanceof AuthError && err.code === 'tooManyRequests'
            ? t('auth.errors.tooManyRequests')
            : t('auth.errors.requestFailed')
        );
      } finally {
        setBusy(false);
      }
    },
    [email]
  );

  const onVerify = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      setBusy(true);
      setError(null);
      try {
        await verifyCode(email.trim(), code.trim());
        // The token rides in the socket URL, so an already-open anonymous
        // socket has to be replaced rather than reused.
        SocketClient.getInstance().reconnect();
        navigate('/', { replace: true });
      } catch (err) {
        const code_ = err instanceof AuthError ? err.code : 'unknownError';
        setError(t(`auth.errors.${code_}`, { _: t('auth.errors.invalidCode') }));
      } finally {
        setBusy(false);
      }
    },
    [code, email, navigate]
  );

  return (
    <Screen>
      <Brand>NextinQ</Brand>

      {step === 'email' ? (
        <>
          <Lead>{t('auth.emailLead')}</Lead>
          <Form onSubmit={onRequest}>
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              autoFocus
              required
              placeholder={t('auth.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button type="submit" disabled={busy || !email.trim()}>
              {busy ? t('auth.sending') : t('auth.continue')}
            </Button>
          </Form>
        </>
      ) : (
        <>
          {/* Deliberately does not claim an account exists — the server does
              not say, so neither can the app. */}
          <Lead>{t('auth.codeLead', { email: email.trim() })}</Lead>
          <Form onSubmit={onVerify}>
            <CodeInput
              inputMode="numeric"
              // Lets iOS and Android offer the code straight from the SMS/mail
              // notification instead of making the user switch apps.
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              autoFocus
              required
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
            <Button type="submit" disabled={busy || code.trim().length < 6}>
              {busy ? t('auth.verifying') : t('auth.signIn')}
            </Button>
          </Form>
          <LinkButton type="button" onClick={() => { setStep('email'); setCode(''); setError(null); }}>
            {t('auth.changeEmail')}
          </LinkButton>
        </>
      )}

      {error ? <ErrorText role="alert">{error}</ErrorText> : null}
    </Screen>
  );
}
